import { reactive, inject, provide, onMounted, getCurrentInstance } from 'vue';
import { A as AgentActionKind, g as DefaultStep, R as RadiusKind, M as MeasureUnit, B as ButtonStyleKind, k as Locale, D as DeviceKind, l as activityKindLiteralMap, p as perkTooltipKindLiteralMap, m as LocaleImageKind, C as CountdownKind, n as CountdownTimezone } from './maps.nxkzRTum.js';

var ButtonState = /* @__PURE__ */ ((ButtonState2) => {
  ButtonState2["Active"] = "active";
  ButtonState2["Sending"] = "sending";
  ButtonState2["Success"] = "success";
  ButtonState2["Failure"] = "failure";
  return ButtonState2;
})(ButtonState || {});

const requireFeedbackStepActions = [
  AgentActionKind.SendForm,
  AgentActionKind.SendSaveSearch,
  AgentActionKind.SendSubscribe
];
const requireHideToolActions = [
  // TODO: add empty click here and update button component
  AgentActionKind.ApplyDiscount,
  AgentActionKind.OpenUri,
  AgentActionKind.OpenBookingEngine,
  AgentActionKind.NoAction,
  AgentActionKind.Dismiss
];
const closingToolActions = [
  AgentActionKind.Hide
];
class HandlersStore {
  dataValidators = {};
  state = reactive({
    actions: [],
    buttonState: ButtonState.Active,
    stepActive: DefaultStep.Main,
    stepPrevious: null,
    isFirstStep: true,
    hasShownPromocodeFallback: false,
    hideFunction: () => {
    }
  });
  get buttonState() {
    return this.state.buttonState;
  }
  get isFirstStep() {
    return this.state.isFirstStep;
  }
  get stepActive() {
    return this.state.stepActive;
  }
  get stepPrevious() {
    return this.state.stepPrevious;
  }
  initialize(hideFunction, actions) {
    this.state.hideFunction = hideFunction;
    this.state.actions = actions;
  }
  setStepActive(step, previousStep = null) {
    this.state.stepActive = step;
    this.state.isFirstStep = step === DefaultStep.Main;
    this.state.stepPrevious = previousStep;
  }
  setDataValidator(kind, validateFunction) {
    this.dataValidators[kind] = validateFunction;
  }
  // accep type as generic for return
  async callHandler(kind, moduleId) {
    if (!this.state.actions) return false;
    let handlerData = {};
    const dataValidator = this.dataValidators[kind];
    if (dataValidator) {
      handlerData = await dataValidator();
      if (!handlerData) {
        return false;
      }
    }
    let handlerAction;
    const findCallbackFn = [
      // Some handlers will never come with a moduleId as they do not belong to any content module (dismiss, show..)
      (action) => action.kind === kind,
      // Last hope, look for the None action - the default one
      (action) => action.kind === AgentActionKind.Fallback
    ];
    if (moduleId) {
      findCallbackFn.unshift((action) => action.moduleId === moduleId);
    }
    for (let i = 0; !handlerAction && i < findCallbackFn.length; i += 1) {
      handlerAction = this.state.actions.find(findCallbackFn[i]);
    }
    if (!handlerAction) return false;
    let response = false;
    if (handlerAction.handler) {
      this.state.buttonState = ButtonState.Sending;
      response = await handlerAction.handler(handlerData);
      this.state.buttonState = ButtonState.Active;
    }
    if (handlerAction.kind === AgentActionKind.ApplyDiscount && !response && !this.state.hasShownPromocodeFallback) {
      this.state.hasShownPromocodeFallback = true;
      return false;
    }
    if (requireFeedbackStepActions.includes(kind)) {
      this.state.stepActive = response ? DefaultStep.Success : DefaultStep.Failure;
      this.state.isFirstStep = false;
    }
    if (requireHideToolActions.includes(kind)) {
      this.callHandler(AgentActionKind.Hide);
    }
    if (closingToolActions.includes(kind)) {
      this.state.hideFunction();
    }
    return response;
  }
  goBackToMainStep() {
    this.state.stepPrevious = this.state.stepActive;
    this.state.stepActive = DefaultStep.Main;
    this.state.isFirstStep = true;
  }
  reset() {
    this.state.buttonState = ButtonState.Active;
    this.state.stepActive = DefaultStep.Main;
    this.state.isFirstStep = true;
  }
}

function _typeof(obj) {
  "@babel/helpers - typeof";
  return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function(obj2) {
    return typeof obj2;
  } : function(obj2) {
    return obj2 && "function" == typeof Symbol && obj2.constructor === Symbol && obj2 !== Symbol.prototype ? "symbol" : typeof obj2;
  }, _typeof(obj);
}
var trimLeft = /^\s+/;
var trimRight = /\s+$/;
function tinycolor(color, opts) {
  color = color ? color : "";
  opts = opts || {};
  if (color instanceof tinycolor) {
    return color;
  }
  if (!(this instanceof tinycolor)) {
    return new tinycolor(color, opts);
  }
  var rgb = inputToRGB(color);
  this._originalInput = color, this._r = rgb.r, this._g = rgb.g, this._b = rgb.b, this._a = rgb.a, this._roundA = Math.round(100 * this._a) / 100, this._format = opts.format || rgb.format;
  this._gradientType = opts.gradientType;
  if (this._r < 1) this._r = Math.round(this._r);
  if (this._g < 1) this._g = Math.round(this._g);
  if (this._b < 1) this._b = Math.round(this._b);
  this._ok = rgb.ok;
}
tinycolor.prototype = {
  isDark: function isDark() {
    return this.getBrightness() < 128;
  },
  isLight: function isLight() {
    return !this.isDark();
  },
  isValid: function isValid() {
    return this._ok;
  },
  getOriginalInput: function getOriginalInput() {
    return this._originalInput;
  },
  getFormat: function getFormat() {
    return this._format;
  },
  getAlpha: function getAlpha() {
    return this._a;
  },
  getBrightness: function getBrightness() {
    var rgb = this.toRgb();
    return (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1e3;
  },
  getLuminance: function getLuminance() {
    var rgb = this.toRgb();
    var RsRGB, GsRGB, BsRGB, R, G, B;
    RsRGB = rgb.r / 255;
    GsRGB = rgb.g / 255;
    BsRGB = rgb.b / 255;
    if (RsRGB <= 0.03928) R = RsRGB / 12.92;
    else R = Math.pow((RsRGB + 0.055) / 1.055, 2.4);
    if (GsRGB <= 0.03928) G = GsRGB / 12.92;
    else G = Math.pow((GsRGB + 0.055) / 1.055, 2.4);
    if (BsRGB <= 0.03928) B = BsRGB / 12.92;
    else B = Math.pow((BsRGB + 0.055) / 1.055, 2.4);
    return 0.2126 * R + 0.7152 * G + 0.0722 * B;
  },
  setAlpha: function setAlpha(value) {
    this._a = boundAlpha(value);
    this._roundA = Math.round(100 * this._a) / 100;
    return this;
  },
  toHsv: function toHsv() {
    var hsv = rgbToHsv(this._r, this._g, this._b);
    return {
      h: hsv.h * 360,
      s: hsv.s,
      v: hsv.v,
      a: this._a
    };
  },
  toHsvString: function toHsvString() {
    var hsv = rgbToHsv(this._r, this._g, this._b);
    var h = Math.round(hsv.h * 360), s = Math.round(hsv.s * 100), v = Math.round(hsv.v * 100);
    return this._a == 1 ? "hsv(" + h + ", " + s + "%, " + v + "%)" : "hsva(" + h + ", " + s + "%, " + v + "%, " + this._roundA + ")";
  },
  toHsl: function toHsl() {
    var hsl = rgbToHsl(this._r, this._g, this._b);
    return {
      h: hsl.h * 360,
      s: hsl.s,
      l: hsl.l,
      a: this._a
    };
  },
  toHslString: function toHslString() {
    var hsl = rgbToHsl(this._r, this._g, this._b);
    var h = Math.round(hsl.h * 360), s = Math.round(hsl.s * 100), l = Math.round(hsl.l * 100);
    return this._a == 1 ? "hsl(" + h + ", " + s + "%, " + l + "%)" : "hsla(" + h + ", " + s + "%, " + l + "%, " + this._roundA + ")";
  },
  toHex: function toHex(allow3Char) {
    return rgbToHex(this._r, this._g, this._b, allow3Char);
  },
  toHexString: function toHexString(allow3Char) {
    return "#" + this.toHex(allow3Char);
  },
  toHex8: function toHex8(allow4Char) {
    return rgbaToHex(this._r, this._g, this._b, this._a, allow4Char);
  },
  toHex8String: function toHex8String(allow4Char) {
    return "#" + this.toHex8(allow4Char);
  },
  toRgb: function toRgb() {
    return {
      r: Math.round(this._r),
      g: Math.round(this._g),
      b: Math.round(this._b),
      a: this._a
    };
  },
  toRgbString: function toRgbString() {
    return this._a == 1 ? "rgb(" + Math.round(this._r) + ", " + Math.round(this._g) + ", " + Math.round(this._b) + ")" : "rgba(" + Math.round(this._r) + ", " + Math.round(this._g) + ", " + Math.round(this._b) + ", " + this._roundA + ")";
  },
  toPercentageRgb: function toPercentageRgb() {
    return {
      r: Math.round(bound01(this._r, 255) * 100) + "%",
      g: Math.round(bound01(this._g, 255) * 100) + "%",
      b: Math.round(bound01(this._b, 255) * 100) + "%",
      a: this._a
    };
  },
  toPercentageRgbString: function toPercentageRgbString() {
    return this._a == 1 ? "rgb(" + Math.round(bound01(this._r, 255) * 100) + "%, " + Math.round(bound01(this._g, 255) * 100) + "%, " + Math.round(bound01(this._b, 255) * 100) + "%)" : "rgba(" + Math.round(bound01(this._r, 255) * 100) + "%, " + Math.round(bound01(this._g, 255) * 100) + "%, " + Math.round(bound01(this._b, 255) * 100) + "%, " + this._roundA + ")";
  },
  toName: function toName() {
    if (this._a === 0) {
      return "transparent";
    }
    if (this._a < 1) {
      return false;
    }
    return hexNames[rgbToHex(this._r, this._g, this._b, true)] || false;
  },
  toFilter: function toFilter(secondColor) {
    var hex8String = "#" + rgbaToArgbHex(this._r, this._g, this._b, this._a);
    var secondHex8String = hex8String;
    var gradientType = this._gradientType ? "GradientType = 1, " : "";
    if (secondColor) {
      var s = tinycolor(secondColor);
      secondHex8String = "#" + rgbaToArgbHex(s._r, s._g, s._b, s._a);
    }
    return "progid:DXImageTransform.Microsoft.gradient(" + gradientType + "startColorstr=" + hex8String + ",endColorstr=" + secondHex8String + ")";
  },
  toString: function toString(format) {
    var formatSet = !!format;
    format = format || this._format;
    var formattedString = false;
    var hasAlpha = this._a < 1 && this._a >= 0;
    var needsAlphaFormat = !formatSet && hasAlpha && (format === "hex" || format === "hex6" || format === "hex3" || format === "hex4" || format === "hex8" || format === "name");
    if (needsAlphaFormat) {
      if (format === "name" && this._a === 0) {
        return this.toName();
      }
      return this.toRgbString();
    }
    if (format === "rgb") {
      formattedString = this.toRgbString();
    }
    if (format === "prgb") {
      formattedString = this.toPercentageRgbString();
    }
    if (format === "hex" || format === "hex6") {
      formattedString = this.toHexString();
    }
    if (format === "hex3") {
      formattedString = this.toHexString(true);
    }
    if (format === "hex4") {
      formattedString = this.toHex8String(true);
    }
    if (format === "hex8") {
      formattedString = this.toHex8String();
    }
    if (format === "name") {
      formattedString = this.toName();
    }
    if (format === "hsl") {
      formattedString = this.toHslString();
    }
    if (format === "hsv") {
      formattedString = this.toHsvString();
    }
    return formattedString || this.toHexString();
  },
  clone: function clone() {
    return tinycolor(this.toString());
  },
  _applyModification: function _applyModification(fn, args) {
    var color = fn.apply(null, [this].concat([].slice.call(args)));
    this._r = color._r;
    this._g = color._g;
    this._b = color._b;
    this.setAlpha(color._a);
    return this;
  },
  lighten: function lighten() {
    return this._applyModification(_lighten, arguments);
  },
  brighten: function brighten() {
    return this._applyModification(_brighten, arguments);
  },
  darken: function darken() {
    return this._applyModification(_darken, arguments);
  },
  desaturate: function desaturate() {
    return this._applyModification(_desaturate, arguments);
  },
  saturate: function saturate() {
    return this._applyModification(_saturate, arguments);
  },
  greyscale: function greyscale() {
    return this._applyModification(_greyscale, arguments);
  },
  spin: function spin() {
    return this._applyModification(_spin, arguments);
  },
  _applyCombination: function _applyCombination(fn, args) {
    return fn.apply(null, [this].concat([].slice.call(args)));
  },
  analogous: function analogous() {
    return this._applyCombination(_analogous, arguments);
  },
  complement: function complement() {
    return this._applyCombination(_complement, arguments);
  },
  monochromatic: function monochromatic() {
    return this._applyCombination(_monochromatic, arguments);
  },
  splitcomplement: function splitcomplement() {
    return this._applyCombination(_splitcomplement, arguments);
  },
  // Disabled until https://github.com/bgrins/TinyColor/issues/254
  // polyad: function (number) {
  //   return this._applyCombination(polyad, [number]);
  // },
  triad: function triad() {
    return this._applyCombination(polyad, [3]);
  },
  tetrad: function tetrad() {
    return this._applyCombination(polyad, [4]);
  }
};
tinycolor.fromRatio = function(color, opts) {
  if (_typeof(color) == "object") {
    var newColor = {};
    for (var i in color) {
      if (color.hasOwnProperty(i)) {
        if (i === "a") {
          newColor[i] = color[i];
        } else {
          newColor[i] = convertToPercentage(color[i]);
        }
      }
    }
    color = newColor;
  }
  return tinycolor(color, opts);
};
function inputToRGB(color) {
  var rgb = {
    r: 0,
    g: 0,
    b: 0
  };
  var a = 1;
  var s = null;
  var v = null;
  var l = null;
  var ok = false;
  var format = false;
  if (typeof color == "string") {
    color = stringInputToObject(color);
  }
  if (_typeof(color) == "object") {
    if (isValidCSSUnit(color.r) && isValidCSSUnit(color.g) && isValidCSSUnit(color.b)) {
      rgb = rgbToRgb(color.r, color.g, color.b);
      ok = true;
      format = String(color.r).substr(-1) === "%" ? "prgb" : "rgb";
    } else if (isValidCSSUnit(color.h) && isValidCSSUnit(color.s) && isValidCSSUnit(color.v)) {
      s = convertToPercentage(color.s);
      v = convertToPercentage(color.v);
      rgb = hsvToRgb(color.h, s, v);
      ok = true;
      format = "hsv";
    } else if (isValidCSSUnit(color.h) && isValidCSSUnit(color.s) && isValidCSSUnit(color.l)) {
      s = convertToPercentage(color.s);
      l = convertToPercentage(color.l);
      rgb = hslToRgb(color.h, s, l);
      ok = true;
      format = "hsl";
    }
    if (color.hasOwnProperty("a")) {
      a = color.a;
    }
  }
  a = boundAlpha(a);
  return {
    ok,
    format: color.format || format,
    r: Math.min(255, Math.max(rgb.r, 0)),
    g: Math.min(255, Math.max(rgb.g, 0)),
    b: Math.min(255, Math.max(rgb.b, 0)),
    a
  };
}
function rgbToRgb(r, g, b) {
  return {
    r: bound01(r, 255) * 255,
    g: bound01(g, 255) * 255,
    b: bound01(b, 255) * 255
  };
}
function rgbToHsl(r, g, b) {
  r = bound01(r, 255);
  g = bound01(g, 255);
  b = bound01(b, 255);
  var max = Math.max(r, g, b), min = Math.min(r, g, b);
  var h, s, l = (max + min) / 2;
  if (max == min) {
    h = s = 0;
  } else {
    var d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }
  return {
    h,
    s,
    l
  };
}
function hslToRgb(h, s, l) {
  var r, g, b;
  h = bound01(h, 360);
  s = bound01(s, 100);
  l = bound01(l, 100);
  function hue2rgb(p2, q2, t) {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p2 + (q2 - p2) * 6 * t;
    if (t < 1 / 2) return q2;
    if (t < 2 / 3) return p2 + (q2 - p2) * (2 / 3 - t) * 6;
    return p2;
  }
  if (s === 0) {
    r = g = b = l;
  } else {
    var q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    var p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  return {
    r: r * 255,
    g: g * 255,
    b: b * 255
  };
}
function rgbToHsv(r, g, b) {
  r = bound01(r, 255);
  g = bound01(g, 255);
  b = bound01(b, 255);
  var max = Math.max(r, g, b), min = Math.min(r, g, b);
  var h, s, v = max;
  var d = max - min;
  s = max === 0 ? 0 : d / max;
  if (max == min) {
    h = 0;
  } else {
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }
  return {
    h,
    s,
    v
  };
}
function hsvToRgb(h, s, v) {
  h = bound01(h, 360) * 6;
  s = bound01(s, 100);
  v = bound01(v, 100);
  var i = Math.floor(h), f = h - i, p = v * (1 - s), q = v * (1 - f * s), t = v * (1 - (1 - f) * s), mod = i % 6, r = [v, q, p, p, t, v][mod], g = [t, v, v, q, p, p][mod], b = [p, p, t, v, v, q][mod];
  return {
    r: r * 255,
    g: g * 255,
    b: b * 255
  };
}
function rgbToHex(r, g, b, allow3Char) {
  var hex = [pad2(Math.round(r).toString(16)), pad2(Math.round(g).toString(16)), pad2(Math.round(b).toString(16))];
  if (allow3Char && hex[0].charAt(0) == hex[0].charAt(1) && hex[1].charAt(0) == hex[1].charAt(1) && hex[2].charAt(0) == hex[2].charAt(1)) {
    return hex[0].charAt(0) + hex[1].charAt(0) + hex[2].charAt(0);
  }
  return hex.join("");
}
function rgbaToHex(r, g, b, a, allow4Char) {
  var hex = [pad2(Math.round(r).toString(16)), pad2(Math.round(g).toString(16)), pad2(Math.round(b).toString(16)), pad2(convertDecimalToHex(a))];
  if (allow4Char && hex[0].charAt(0) == hex[0].charAt(1) && hex[1].charAt(0) == hex[1].charAt(1) && hex[2].charAt(0) == hex[2].charAt(1) && hex[3].charAt(0) == hex[3].charAt(1)) {
    return hex[0].charAt(0) + hex[1].charAt(0) + hex[2].charAt(0) + hex[3].charAt(0);
  }
  return hex.join("");
}
function rgbaToArgbHex(r, g, b, a) {
  var hex = [pad2(convertDecimalToHex(a)), pad2(Math.round(r).toString(16)), pad2(Math.round(g).toString(16)), pad2(Math.round(b).toString(16))];
  return hex.join("");
}
tinycolor.equals = function(color1, color2) {
  if (!color1 || !color2) return false;
  return tinycolor(color1).toRgbString() == tinycolor(color2).toRgbString();
};
tinycolor.random = function() {
  return tinycolor.fromRatio({
    r: Math.random(),
    g: Math.random(),
    b: Math.random()
  });
};
function _desaturate(color, amount) {
  amount = amount === 0 ? 0 : amount || 10;
  var hsl = tinycolor(color).toHsl();
  hsl.s -= amount / 100;
  hsl.s = clamp01(hsl.s);
  return tinycolor(hsl);
}
function _saturate(color, amount) {
  amount = amount === 0 ? 0 : amount || 10;
  var hsl = tinycolor(color).toHsl();
  hsl.s += amount / 100;
  hsl.s = clamp01(hsl.s);
  return tinycolor(hsl);
}
function _greyscale(color) {
  return tinycolor(color).desaturate(100);
}
function _lighten(color, amount) {
  amount = amount === 0 ? 0 : amount || 10;
  var hsl = tinycolor(color).toHsl();
  hsl.l += amount / 100;
  hsl.l = clamp01(hsl.l);
  return tinycolor(hsl);
}
function _brighten(color, amount) {
  amount = amount === 0 ? 0 : amount || 10;
  var rgb = tinycolor(color).toRgb();
  rgb.r = Math.max(0, Math.min(255, rgb.r - Math.round(255 * -(amount / 100))));
  rgb.g = Math.max(0, Math.min(255, rgb.g - Math.round(255 * -(amount / 100))));
  rgb.b = Math.max(0, Math.min(255, rgb.b - Math.round(255 * -(amount / 100))));
  return tinycolor(rgb);
}
function _darken(color, amount) {
  amount = amount === 0 ? 0 : amount || 10;
  var hsl = tinycolor(color).toHsl();
  hsl.l -= amount / 100;
  hsl.l = clamp01(hsl.l);
  return tinycolor(hsl);
}
function _spin(color, amount) {
  var hsl = tinycolor(color).toHsl();
  var hue = (hsl.h + amount) % 360;
  hsl.h = hue < 0 ? 360 + hue : hue;
  return tinycolor(hsl);
}
function _complement(color) {
  var hsl = tinycolor(color).toHsl();
  hsl.h = (hsl.h + 180) % 360;
  return tinycolor(hsl);
}
function polyad(color, number) {
  if (isNaN(number) || number <= 0) {
    throw new Error("Argument to polyad must be a positive number");
  }
  var hsl = tinycolor(color).toHsl();
  var result = [tinycolor(color)];
  var step = 360 / number;
  for (var i = 1; i < number; i++) {
    result.push(tinycolor({
      h: (hsl.h + i * step) % 360,
      s: hsl.s,
      l: hsl.l
    }));
  }
  return result;
}
function _splitcomplement(color) {
  var hsl = tinycolor(color).toHsl();
  var h = hsl.h;
  return [tinycolor(color), tinycolor({
    h: (h + 72) % 360,
    s: hsl.s,
    l: hsl.l
  }), tinycolor({
    h: (h + 216) % 360,
    s: hsl.s,
    l: hsl.l
  })];
}
function _analogous(color, results, slices) {
  results = results || 6;
  slices = slices || 30;
  var hsl = tinycolor(color).toHsl();
  var part = 360 / slices;
  var ret = [tinycolor(color)];
  for (hsl.h = (hsl.h - (part * results >> 1) + 720) % 360; --results; ) {
    hsl.h = (hsl.h + part) % 360;
    ret.push(tinycolor(hsl));
  }
  return ret;
}
function _monochromatic(color, results) {
  results = results || 6;
  var hsv = tinycolor(color).toHsv();
  var h = hsv.h, s = hsv.s, v = hsv.v;
  var ret = [];
  var modification = 1 / results;
  while (results--) {
    ret.push(tinycolor({
      h,
      s,
      v
    }));
    v = (v + modification) % 1;
  }
  return ret;
}
tinycolor.mix = function(color1, color2, amount) {
  amount = amount === 0 ? 0 : amount || 50;
  var rgb1 = tinycolor(color1).toRgb();
  var rgb2 = tinycolor(color2).toRgb();
  var p = amount / 100;
  var rgba = {
    r: (rgb2.r - rgb1.r) * p + rgb1.r,
    g: (rgb2.g - rgb1.g) * p + rgb1.g,
    b: (rgb2.b - rgb1.b) * p + rgb1.b,
    a: (rgb2.a - rgb1.a) * p + rgb1.a
  };
  return tinycolor(rgba);
};
tinycolor.readability = function(color1, color2) {
  var c1 = tinycolor(color1);
  var c2 = tinycolor(color2);
  return (Math.max(c1.getLuminance(), c2.getLuminance()) + 0.05) / (Math.min(c1.getLuminance(), c2.getLuminance()) + 0.05);
};
tinycolor.isReadable = function(color1, color2, wcag2) {
  var readability = tinycolor.readability(color1, color2);
  var wcag2Parms, out;
  out = false;
  wcag2Parms = validateWCAG2Parms(wcag2);
  switch (wcag2Parms.level + wcag2Parms.size) {
    case "AAsmall":
    case "AAAlarge":
      out = readability >= 4.5;
      break;
    case "AAlarge":
      out = readability >= 3;
      break;
    case "AAAsmall":
      out = readability >= 7;
      break;
  }
  return out;
};
tinycolor.mostReadable = function(baseColor, colorList, args) {
  var bestColor = null;
  var bestScore = 0;
  var readability;
  var includeFallbackColors, level, size;
  args = args || {};
  includeFallbackColors = args.includeFallbackColors;
  level = args.level;
  size = args.size;
  for (var i = 0; i < colorList.length; i++) {
    readability = tinycolor.readability(baseColor, colorList[i]);
    if (readability > bestScore) {
      bestScore = readability;
      bestColor = tinycolor(colorList[i]);
    }
  }
  if (tinycolor.isReadable(baseColor, bestColor, {
    level,
    size
  }) || !includeFallbackColors) {
    return bestColor;
  } else {
    args.includeFallbackColors = false;
    return tinycolor.mostReadable(baseColor, ["#fff", "#000"], args);
  }
};
var names = tinycolor.names = {
  aliceblue: "f0f8ff",
  antiquewhite: "faebd7",
  aqua: "0ff",
  aquamarine: "7fffd4",
  azure: "f0ffff",
  beige: "f5f5dc",
  bisque: "ffe4c4",
  black: "000",
  blanchedalmond: "ffebcd",
  blue: "00f",
  blueviolet: "8a2be2",
  brown: "a52a2a",
  burlywood: "deb887",
  burntsienna: "ea7e5d",
  cadetblue: "5f9ea0",
  chartreuse: "7fff00",
  chocolate: "d2691e",
  coral: "ff7f50",
  cornflowerblue: "6495ed",
  cornsilk: "fff8dc",
  crimson: "dc143c",
  cyan: "0ff",
  darkblue: "00008b",
  darkcyan: "008b8b",
  darkgoldenrod: "b8860b",
  darkgray: "a9a9a9",
  darkgreen: "006400",
  darkgrey: "a9a9a9",
  darkkhaki: "bdb76b",
  darkmagenta: "8b008b",
  darkolivegreen: "556b2f",
  darkorange: "ff8c00",
  darkorchid: "9932cc",
  darkred: "8b0000",
  darksalmon: "e9967a",
  darkseagreen: "8fbc8f",
  darkslateblue: "483d8b",
  darkslategray: "2f4f4f",
  darkslategrey: "2f4f4f",
  darkturquoise: "00ced1",
  darkviolet: "9400d3",
  deeppink: "ff1493",
  deepskyblue: "00bfff",
  dimgray: "696969",
  dimgrey: "696969",
  dodgerblue: "1e90ff",
  firebrick: "b22222",
  floralwhite: "fffaf0",
  forestgreen: "228b22",
  fuchsia: "f0f",
  gainsboro: "dcdcdc",
  ghostwhite: "f8f8ff",
  gold: "ffd700",
  goldenrod: "daa520",
  gray: "808080",
  green: "008000",
  greenyellow: "adff2f",
  grey: "808080",
  honeydew: "f0fff0",
  hotpink: "ff69b4",
  indianred: "cd5c5c",
  indigo: "4b0082",
  ivory: "fffff0",
  khaki: "f0e68c",
  lavender: "e6e6fa",
  lavenderblush: "fff0f5",
  lawngreen: "7cfc00",
  lemonchiffon: "fffacd",
  lightblue: "add8e6",
  lightcoral: "f08080",
  lightcyan: "e0ffff",
  lightgoldenrodyellow: "fafad2",
  lightgray: "d3d3d3",
  lightgreen: "90ee90",
  lightgrey: "d3d3d3",
  lightpink: "ffb6c1",
  lightsalmon: "ffa07a",
  lightseagreen: "20b2aa",
  lightskyblue: "87cefa",
  lightslategray: "789",
  lightslategrey: "789",
  lightsteelblue: "b0c4de",
  lightyellow: "ffffe0",
  lime: "0f0",
  limegreen: "32cd32",
  linen: "faf0e6",
  magenta: "f0f",
  maroon: "800000",
  mediumaquamarine: "66cdaa",
  mediumblue: "0000cd",
  mediumorchid: "ba55d3",
  mediumpurple: "9370db",
  mediumseagreen: "3cb371",
  mediumslateblue: "7b68ee",
  mediumspringgreen: "00fa9a",
  mediumturquoise: "48d1cc",
  mediumvioletred: "c71585",
  midnightblue: "191970",
  mintcream: "f5fffa",
  mistyrose: "ffe4e1",
  moccasin: "ffe4b5",
  navajowhite: "ffdead",
  navy: "000080",
  oldlace: "fdf5e6",
  olive: "808000",
  olivedrab: "6b8e23",
  orange: "ffa500",
  orangered: "ff4500",
  orchid: "da70d6",
  palegoldenrod: "eee8aa",
  palegreen: "98fb98",
  paleturquoise: "afeeee",
  palevioletred: "db7093",
  papayawhip: "ffefd5",
  peachpuff: "ffdab9",
  peru: "cd853f",
  pink: "ffc0cb",
  plum: "dda0dd",
  powderblue: "b0e0e6",
  purple: "800080",
  rebeccapurple: "663399",
  red: "f00",
  rosybrown: "bc8f8f",
  royalblue: "4169e1",
  saddlebrown: "8b4513",
  salmon: "fa8072",
  sandybrown: "f4a460",
  seagreen: "2e8b57",
  seashell: "fff5ee",
  sienna: "a0522d",
  silver: "c0c0c0",
  skyblue: "87ceeb",
  slateblue: "6a5acd",
  slategray: "708090",
  slategrey: "708090",
  snow: "fffafa",
  springgreen: "00ff7f",
  steelblue: "4682b4",
  tan: "d2b48c",
  teal: "008080",
  thistle: "d8bfd8",
  tomato: "ff6347",
  turquoise: "40e0d0",
  violet: "ee82ee",
  wheat: "f5deb3",
  white: "fff",
  whitesmoke: "f5f5f5",
  yellow: "ff0",
  yellowgreen: "9acd32"
};
var hexNames = tinycolor.hexNames = flip(names);
function flip(o) {
  var flipped = {};
  for (var i in o) {
    if (o.hasOwnProperty(i)) {
      flipped[o[i]] = i;
    }
  }
  return flipped;
}
function boundAlpha(a) {
  a = parseFloat(a);
  if (isNaN(a) || a < 0 || a > 1) {
    a = 1;
  }
  return a;
}
function bound01(n, max) {
  if (isOnePointZero(n)) n = "100%";
  var processPercent = isPercentage(n);
  n = Math.min(max, Math.max(0, parseFloat(n)));
  if (processPercent) {
    n = parseInt(n * max, 10) / 100;
  }
  if (Math.abs(n - max) < 1e-6) {
    return 1;
  }
  return n % max / parseFloat(max);
}
function clamp01(val) {
  return Math.min(1, Math.max(0, val));
}
function parseIntFromHex(val) {
  return parseInt(val, 16);
}
function isOnePointZero(n) {
  return typeof n == "string" && n.indexOf(".") != -1 && parseFloat(n) === 1;
}
function isPercentage(n) {
  return typeof n === "string" && n.indexOf("%") != -1;
}
function pad2(c) {
  return c.length == 1 ? "0" + c : "" + c;
}
function convertToPercentage(n) {
  if (n <= 1) {
    n = n * 100 + "%";
  }
  return n;
}
function convertDecimalToHex(d) {
  return Math.round(parseFloat(d) * 255).toString(16);
}
function convertHexToDecimal(h) {
  return parseIntFromHex(h) / 255;
}
var matchers = function() {
  var CSS_INTEGER = "[-\\+]?\\d+%?";
  var CSS_NUMBER = "[-\\+]?\\d*\\.\\d+%?";
  var CSS_UNIT = "(?:" + CSS_NUMBER + ")|(?:" + CSS_INTEGER + ")";
  var PERMISSIVE_MATCH3 = "[\\s|\\(]+(" + CSS_UNIT + ")[,|\\s]+(" + CSS_UNIT + ")[,|\\s]+(" + CSS_UNIT + ")\\s*\\)?";
  var PERMISSIVE_MATCH4 = "[\\s|\\(]+(" + CSS_UNIT + ")[,|\\s]+(" + CSS_UNIT + ")[,|\\s]+(" + CSS_UNIT + ")[,|\\s]+(" + CSS_UNIT + ")\\s*\\)?";
  return {
    CSS_UNIT: new RegExp(CSS_UNIT),
    rgb: new RegExp("rgb" + PERMISSIVE_MATCH3),
    rgba: new RegExp("rgba" + PERMISSIVE_MATCH4),
    hsl: new RegExp("hsl" + PERMISSIVE_MATCH3),
    hsla: new RegExp("hsla" + PERMISSIVE_MATCH4),
    hsv: new RegExp("hsv" + PERMISSIVE_MATCH3),
    hsva: new RegExp("hsva" + PERMISSIVE_MATCH4),
    hex3: /^#?([0-9a-fA-F]{1})([0-9a-fA-F]{1})([0-9a-fA-F]{1})$/,
    hex6: /^#?([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/,
    hex4: /^#?([0-9a-fA-F]{1})([0-9a-fA-F]{1})([0-9a-fA-F]{1})([0-9a-fA-F]{1})$/,
    hex8: /^#?([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/
  };
}();
function isValidCSSUnit(color) {
  return !!matchers.CSS_UNIT.exec(color);
}
function stringInputToObject(color) {
  color = color.replace(trimLeft, "").replace(trimRight, "").toLowerCase();
  var named = false;
  if (names[color]) {
    color = names[color];
    named = true;
  } else if (color == "transparent") {
    return {
      r: 0,
      g: 0,
      b: 0,
      a: 0,
      format: "name"
    };
  }
  var match;
  if (match = matchers.rgb.exec(color)) {
    return {
      r: match[1],
      g: match[2],
      b: match[3]
    };
  }
  if (match = matchers.rgba.exec(color)) {
    return {
      r: match[1],
      g: match[2],
      b: match[3],
      a: match[4]
    };
  }
  if (match = matchers.hsl.exec(color)) {
    return {
      h: match[1],
      s: match[2],
      l: match[3]
    };
  }
  if (match = matchers.hsla.exec(color)) {
    return {
      h: match[1],
      s: match[2],
      l: match[3],
      a: match[4]
    };
  }
  if (match = matchers.hsv.exec(color)) {
    return {
      h: match[1],
      s: match[2],
      v: match[3]
    };
  }
  if (match = matchers.hsva.exec(color)) {
    return {
      h: match[1],
      s: match[2],
      v: match[3],
      a: match[4]
    };
  }
  if (match = matchers.hex8.exec(color)) {
    return {
      r: parseIntFromHex(match[1]),
      g: parseIntFromHex(match[2]),
      b: parseIntFromHex(match[3]),
      a: convertHexToDecimal(match[4]),
      format: named ? "name" : "hex8"
    };
  }
  if (match = matchers.hex6.exec(color)) {
    return {
      r: parseIntFromHex(match[1]),
      g: parseIntFromHex(match[2]),
      b: parseIntFromHex(match[3]),
      format: named ? "name" : "hex"
    };
  }
  if (match = matchers.hex4.exec(color)) {
    return {
      r: parseIntFromHex(match[1] + "" + match[1]),
      g: parseIntFromHex(match[2] + "" + match[2]),
      b: parseIntFromHex(match[3] + "" + match[3]),
      a: convertHexToDecimal(match[4] + "" + match[4]),
      format: named ? "name" : "hex8"
    };
  }
  if (match = matchers.hex3.exec(color)) {
    return {
      r: parseIntFromHex(match[1] + "" + match[1]),
      g: parseIntFromHex(match[2] + "" + match[2]),
      b: parseIntFromHex(match[3] + "" + match[3]),
      format: named ? "name" : "hex"
    };
  }
  return false;
}
function validateWCAG2Parms(parms) {
  var level, size;
  parms = parms || {
    level: "AA",
    size: "small"
  };
  level = (parms.level || "AA").toUpperCase();
  size = (parms.size || "small").toLowerCase();
  if (level !== "AA" && level !== "AAA") {
    level = "AA";
  }
  if (size !== "small" && size !== "large") {
    size = "small";
  }
  return {
    level,
    size
  };
}

function hexToRgba(hexStr, alpha = 1) {
  let hex = hexStr.replace("#", "");
  if (![3, 6].includes(hex.length)) {
    return null;
  }
  if (hex.length === 3) {
    hex = hex.split("").map((hexChar) => hexChar + hexChar).join("");
  }
  const c = parseInt(hex, 16);
  return `rgba(${c >> 16 & 255},${c >> 8 & 255},${c & 255},${alpha})`;
}
function toPascalCase(str) {
  const tempStr = str.replace(/[-_\s]+(.)?/g, (_match, ch) => ch ? ch.toUpperCase() : "");
  return tempStr.substring(0, 1).toUpperCase() + tempStr.substring(1);
}
function toKebabCase(str) {
  return str.replace(/([a-z])([A-Z])/g, "$1-$2").replace(/[\s_]+/g, "-").toLowerCase();
}
const camelToSnakeCase = (str) => str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
function getCornerRadius(refKind) {
  const kind = refKind?.kind || RadiusKind.Rounded;
  switch (kind) {
    case RadiusKind.Custom:
      const customKind = refKind;
      return `${customKind.value}${customKind.format}`;
    case RadiusKind.Square:
      return "0px";
    case RadiusKind.Medium:
      return "6px";
    case RadiusKind.Rounded:
    default:
      return "16px";
  }
}
function appendPx(num = 0) {
  return `${num}px`;
}
function appendPercent(num = 0) {
  return `${num}%`;
}
function appendEm(num = 0) {
  return `${num}em`;
}
function getEmByPercent(percent) {
  return `${percent * 6 / 100}em`;
}
function getPaddingLiteral({
  format,
  top,
  right,
  bottom,
  left
}) {
  switch (format) {
    case MeasureUnit.Pixels:
      return `${top}${format} ${right}${format} ${bottom}${format} ${left}${format}`;
    case MeasureUnit.Em:
    default:
      return `${getEmByPercent(top)} ${getEmByPercent(right)} ${getEmByPercent(bottom)} ${getEmByPercent(left)}`;
  }
}
function isObj(object) {
  return object?.constructor === Object;
}
function padNumber(num, maxLength = 2) {
  return num.toFixed(0).padStart(maxLength, "0");
}
function parseTimeLeft(secondsLeft) {
  if (secondsLeft < 0) {
    return {
      hours: "00",
      minutes: "00",
      seconds: "00"
    };
  }
  const mins = Math.floor(secondsLeft / 60);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  const blocks = {
    // Warn: order of key-values of the object *is* critical for display
    days: padNumber(days),
    hours: padNumber(hours % 24),
    minutes: padNumber(mins % 60),
    seconds: padNumber(secondsLeft % 60)
  };
  if (!days) {
    delete blocks.days;
  } else {
    delete blocks.seconds;
  }
  return blocks;
}
function sanitizeMessage(message) {
  if (!message) return "";
  const tempElement = document.createElement("div");
  tempElement.innerHTML = message;
  return tempElement.textContent || tempElement.innerText;
}

const I = Symbol("i18n");
function O(t, e) {
  return e ? Object.keys(e).reduce(
    (n, r) => n.replace(`{${r}}`, e[r].toString()),
    t
  ) : t;
}
var y = {};
Object.defineProperty(y, "__esModule", { value: !0 });
y.TokenData = void 0;
y.parse = b;
y.compile = Y;
y.match = te;
y.pathToRegexp = oe;
const W = "/", j = (t) => t, X = new RegExp("^\\p{XID_Continue}$", "u"), C = "https://git.new/pathToRegexpError", Z = {
  "!": "!",
  "@": "@",
  ";": ";",
  ",": ",",
  "*": "*",
  "+": "+",
  "?": "?",
  "{": "{",
  "}": "}"
};
function Q(t) {
  const e = [...t], n = [];
  let r = 0;
  for (; r < e.length; ) {
    const s = e[r], a = Z[s];
    if (a) {
      n.push({ type: a, index: r++, value: s });
      continue;
    }
    if (s === "\\") {
      n.push({ type: "ESCAPED", index: r++, value: e[r++] });
      continue;
    }
    if (s === ":") {
      let l = "";
      for (; X.test(e[++r]); )
        l += e[r];
      if (!l)
        throw new TypeError(`Missing parameter name at ${r}`);
      n.push({ type: "NAME", index: r, value: l });
      continue;
    }
    if (s === "(") {
      const l = r++;
      let c = 1, o = "";
      if (e[r] === "?")
        throw new TypeError(`Pattern cannot start with "?" at ${r}`);
      for (; r < e.length; ) {
        if (e[r] === "\\") {
          o += e[r++] + e[r++];
          continue;
        }
        if (e[r] === ")") {
          if (c--, c === 0) {
            r++;
            break;
          }
        } else if (e[r] === "(" && (c++, e[r + 1] !== "?"))
          throw new TypeError(`Capturing groups are not allowed at ${r}`);
        o += e[r++];
      }
      if (c)
        throw new TypeError(`Unbalanced pattern at ${l}`);
      if (!o)
        throw new TypeError(`Missing pattern at ${l}`);
      n.push({ type: "PATTERN", index: r, value: o });
      continue;
    }
    n.push({ type: "CHAR", index: r, value: e[r++] });
  }
  return n.push({ type: "END", index: r, value: "" }), new V(n);
}
class V {
  constructor(e) {
    this.tokens = e, this.index = 0;
  }
  peek() {
    return this.tokens[this.index];
  }
  tryConsume(e) {
    const n = this.peek();
    if (n.type === e)
      return this.index++, n.value;
  }
  consume(e) {
    const n = this.tryConsume(e);
    if (n !== void 0)
      return n;
    const { type: r, index: s } = this.peek();
    throw new TypeError(`Unexpected ${r} at ${s}, expected ${e}: ${C}`);
  }
  text() {
    let e = "", n;
    for (; n = this.tryConsume("CHAR") || this.tryConsume("ESCAPED"); )
      e += n;
    return e;
  }
  modifier() {
    return this.tryConsume("?") || this.tryConsume("*") || this.tryConsume("+");
  }
}
class T {
  constructor(e, n) {
    this.tokens = e, this.delimiter = n;
  }
}
y.TokenData = T;
function b(t, e = {}) {
  const { encodePath: n = j, delimiter: r = n(W) } = e, s = [], a = Q(t);
  let l = 0;
  do {
    const c = a.text();
    c && s.push(n(c));
    const o = a.tryConsume("NAME"), p = a.tryConsume("PATTERN");
    if (o || p) {
      s.push({
        name: o || String(l++),
        pattern: p
      });
      const i = a.peek();
      if (i.type === "*")
        throw new TypeError(`Unexpected * at ${i.index}, you probably want \`/*\` or \`{/:foo}*\`: ${C}`);
      continue;
    }
    if (a.tryConsume("*")) {
      s.push({
        name: String(l++),
        pattern: `(?:(?!${E(r)}).)*`,
        modifier: "*",
        separator: r
      });
      continue;
    }
    if (a.tryConsume("{")) {
      const i = a.text(), u = a.tryConsume("NAME"), f = a.tryConsume("PATTERN"), m = a.text(), d = a.tryConsume(";") && a.text();
      a.consume("}");
      const h = a.modifier();
      s.push({
        name: u || (f ? String(l++) : ""),
        prefix: n(i),
        suffix: n(m),
        pattern: f,
        modifier: h,
        separator: d
      });
      continue;
    }
    a.consume("END");
    break;
  } while (!0);
  return new T(s, r);
}
function Y(t, e = {}) {
  const n = t instanceof T ? t : b(t, e);
  return ee(n, e);
}
function K(t, e) {
  if (typeof t == "string")
    return () => t;
  const n = e || j, r = t.modifier === "+" || t.modifier === "*", s = t.modifier === "?" || t.modifier === "*", { prefix: a = "", suffix: l = "", separator: c = l + a } = t;
  if (e && r) {
    const p = (u, f) => {
      if (typeof u != "string")
        throw new TypeError(`Expected "${t.name}/${f}" to be a string`);
      return n(u);
    }, i = (u) => {
      if (!Array.isArray(u))
        throw new TypeError(`Expected "${t.name}" to be an array`);
      return u.length === 0 ? "" : a + u.map(p).join(c) + l;
    };
    return s ? (u) => {
      const f = u[t.name];
      return f == null ? "" : f.length ? i(f) : "";
    } : (u) => {
      const f = u[t.name];
      return i(f);
    };
  }
  const o = (p) => {
    if (typeof p != "string")
      throw new TypeError(`Expected "${t.name}" to be a string`);
    return a + n(p) + l;
  };
  return s ? (p) => {
    const i = p[t.name];
    return i == null ? "" : o(i);
  } : (p) => {
    const i = p[t.name];
    return o(i);
  };
}
function ee(t, e) {
  const { encode: n = encodeURIComponent, loose: r = !0, validate: s = !0, strict: a = !1 } = e, l = M(e), c = S(r, t.delimiter), o = N(t, c, [], l, a), p = t.tokens.map((i, u) => {
    const f = K(i, n);
    if (!s || typeof i == "string")
      return f;
    const m = new RegExp(`^${o[u]}$`, l);
    return (d) => {
      const h = f(d);
      if (!m.test(h))
        throw new TypeError(`Invalid value for "${i.name}": ${JSON.stringify(h)}`);
      return h;
    };
  });
  return function(i = {}) {
    let u = "";
    for (const f of p)
      u += f(i);
    return u;
  };
}
function te(t, e = {}) {
  const { decode: n = decodeURIComponent, loose: r = !0 } = e, s = t instanceof T ? t : b(t, e), a = S(r, s.delimiter), l = [], c = _(s, l, e), o = l.map((p) => {
    if (n && (p.modifier === "+" || p.modifier === "*")) {
      const { prefix: i = "", suffix: u = "", separator: f = u + i } = p, m = new RegExp(a(f), "g");
      return (d) => d.split(m).map(n);
    }
    return n || j;
  });
  return function(p) {
    const i = c.exec(p);
    if (!i)
      return !1;
    const { 0: u, index: f } = i, m = /* @__PURE__ */ Object.create(null);
    for (let d = 1; d < i.length; d++) {
      if (i[d] === void 0)
        continue;
      const h = l[d - 1], w = o[d - 1];
      m[h.name] = w(i[d]);
    }
    return { path: u, index: f, params: m };
  };
}
function E(t) {
  return t.replace(/([.+*?^${}()[\]|/\\])/g, "\\$1");
}
function ne(t, e) {
  const n = E(t);
  return e ? `(?:${n})+(?!${n})` : n;
}
function S(t, e) {
  if (!t)
    return E;
  const n = new RegExp(`(?:(?!${E(e)}).)+|(.)`, "g");
  return (r) => r.replace(n, ne);
}
function M(t) {
  return t.sensitive ? "" : "i";
}
function _(t, e, n) {
  const { trailing: r = !0, loose: s = !0, start: a = !0, end: l = !0, strict: c = !1 } = n, o = M(n), p = S(s, t.delimiter), i = N(t, p, e, o, c);
  let u = a ? "^" : "";
  return u += i.join(""), r && (u += `(?:${p(t.delimiter)})?`), u += l ? "$" : `(?=${E(t.delimiter)}|$)`, new RegExp(u, o);
}
function N(t, e, n, r, s) {
  const a = `(?:(?!${E(t.delimiter)}).)+?`;
  let l = "", c = !0;
  return t.tokens.map((o, p) => {
    if (typeof o == "string")
      return l = o, e(o);
    const { prefix: i = "", suffix: u = "", separator: f = u + i, modifier: m = "" } = o, d = e(i), h = e(u);
    if (o.name) {
      const w = o.pattern ? `(?:${o.pattern})` : a, v = re(w, o.name, r);
      if (c || (c = P(v, i || l)), !c)
        throw new TypeError(`Ambiguous pattern for "${o.name}": ${C}`);
      if (c = !s || P(v, u), l = "", n.push(o), m === "+" || m === "*") {
        const D = m === "*" ? "?" : "", A = e(f);
        if (!A)
          throw new TypeError(`Missing separator for "${o.name}": ${C}`);
        if (c || (c = !s || P(v, f)), !c)
          throw new TypeError(`Ambiguous pattern for "${o.name}" separator: ${C}`);
        return c = !s, `(?:${d}(${w}(?:${A}${w})*)${h})${D}`;
      }
      return `(?:${d}(${w})${h})${m}`;
    }
    return `(?:${d}${h})${m}`;
  });
}
function re(t, e, n) {
  try {
    return new RegExp(`^${t}$`, n);
  } catch (r) {
    throw new TypeError(`Invalid pattern for "${e}": ${r.message}`);
  }
}
function P(t, e) {
  return e ? !t.test(e) : !1;
}
function oe(t, e = {}) {
  const n = t instanceof T ? t : b(t, e), r = [], s = _(n, r, e);
  return Object.assign(s, { keys: r });
}
var ie = typeof global == "object" && global && global.Object === Object && global, se = typeof self == "object" && self && self.Object === Object && self, ae = ie || se || Function("return this")(), R = ae.Symbol;
R && R.toStringTag;
R && R.toStringTag;
var ue = Function.prototype, ce = ue.toString;
ce.call(Object);
const $ = ({
  langCode: t,
  mapper: e,
  number: n,
  keysLength: r
}) => {
  if (n === 0) return 0;
  const s = new Intl.PluralRules(t).select(n), a = e[s];
  return Math.min(
    r <= Object.keys(e).length ? a - 1 : a,
    r - 1
  );
}, le = (t, e) => $({
  langCode: "en-EN",
  mapper: { one: 1, other: 2 },
  number: t,
  keysLength: e
}), pe = (t, e) => {
  const n = new Intl.PluralRules("ar-EG").select(t), r = {
    zero: 0,
    one: 1,
    two: 2,
    few: 3,
    many: 4,
    other: 5
  }[n];
  return Math.min(r, e - 1);
}, k = (t, e) => $({
  langCode: "ru-RU",
  mapper: { one: 1, few: 2, many: 3 },
  number: t,
  keysLength: e
}), fe = (t, e) => $({
  langCode: "ro-RO",
  mapper: { one: 1, few: 2, other: 3 },
  number: t,
  keysLength: e
}), me = (t, e) => $({
  langCode: "pl-PL",
  mapper: { one: 1, few: 2, many: 3 },
  number: t,
  keysLength: e
}), de = (t, e) => $({
  langCode: "is-IS",
  mapper: { one: 1, other: 2 },
  number: t,
  keysLength: e
}), he = (t, e) => $({
  langCode: "hr-HR",
  mapper: { one: 1, few: 2, other: 3 },
  number: t,
  keysLength: e
}), ge = (t, e) => $({
  langCode: "cs-CZ",
  mapper: { one: 1, few: 2, other: 3 },
  number: t,
  keysLength: e
}), g = () => 0, xe = {
  ar: pe,
  cs: ge,
  hr: he,
  id: g,
  is: de,
  ja: g,
  ko: g,
  lt: k,
  pl: me,
  ro: fe,
  ru: k,
  uk: k,
  th: g,
  vi: g,
  zh: g,
  "zh-TW": g
};
const $e = () => inject(I, {
  t: (e, n) => O(e, n) || e,
  tc: (e, n, r) => O(e, r) || e
});

function getPluralized(lang, count, phrase, literals, pluralizationRules) {
  const langCode = lang || "en";
  const pluralRule = xe[langCode] ?? le;
  const pluralIndex = pluralRule(count, 2);
  if (!pluralIndex && pluralizationRules === "lastReservation") {
    return phrase.replace(literals.hours, literals.hour);
  }
  if (!pluralIndex && ["nowViewing", "todayViewed", "bookedToday"].includes(pluralizationRules)) {
    return phrase.replace(literals.people, literals.person);
  }
  return phrase;
}

function toTitleCase(text) {
  const smallWords = /^(&|&amp;|a|an|and|as|at|but|by|en|for|if|in|nor|of|on|or|per|the|to|v.?|vs.?|via)$/i;
  const alphanumericPattern = /([A-Za-z0-9\u00C0-\u00FF])/;
  const wordSeparators = /([ :–—-])/;
  return text.split(wordSeparators).map((current, index, array) => {
    if (current.search(smallWords) > -1 && index !== 0 && index !== array.length - 1 && array[index - 3] !== ":" && array[index + 1] !== ":" && (array[index + 1] !== "-" || array[index - 1] === "-" && array[index + 1] === "-")) {
      return current.toLowerCase();
    }
    if (current.substring(1).search(/[A-Z]|\../) > -1) {
      return current;
    }
    if (array[index + 1] === ":" && array[index + 2] !== "") {
      return current;
    }
    return current.replace(alphanumericPattern, (match) => match.toUpperCase());
  }).join("");
}

function parseCssValue(key, value) {
  switch (key) {
    case "opacity":
      return (parseInt(value, 10) / 100).toString();
    case "thickness":
      return appendPx(parseInt(value, 10));
    case "cssName":
      return `'${value}'`;
    default:
      return value;
  }
}
function parseCssObjectValue(key, value) {
  switch (key) {
    case "cornerRadius":
      return getCornerRadius(value);
    case "padding":
      return getPaddingLiteral(value);
    default:
      return null;
  }
}
function getCssVarNames(styles, prefix) {
  return Object.keys(styles).reduce((stylesObj, key) => {
    let value = parseCssValue(key, styles[key]?.toString());
    if (isObj(styles[key])) {
      value = parseCssObjectValue(key, styles[key]);
      if (!value) {
        const newObj = {
          ...stylesObj,
          ...getCssVarNames(styles[key], `${prefix}-${toKebabCase(key)}`)
        };
        return newObj;
      }
    }
    stylesObj[`${prefix}-${toKebabCase(key)}`] = value;
    return stylesObj;
  }, {});
}
class StylesStore {
  rootDom = null;
  state = reactive({
    colors: {},
    fonts: {},
    button: {},
    background: {},
    secondaryButton: {},
    overlay: {}
  });
  get rootElement() {
    return this.rootDom;
  }
  get fonts() {
    return this.state.fonts;
  }
  get backgroundImage() {
    return this.state.background.image?.urls?.edited;
  }
  get backgroundImageAlt() {
    return this.state.background.image?.alt;
  }
  get colors() {
    return this.state.colors;
  }
  get button() {
    return this.state.button;
  }
  initialize(styles, rootDom) {
    this.rootDom = rootDom;
    this.state.colors = styles.colors || {};
    this.state.fonts = {
      ...styles.fonts,
      perks: styles.fonts?.perks ?? styles.fonts?.other,
      countdown: styles.fonts?.countdown ?? styles.fonts?.other
    };
    this.state.button = styles.button || {};
    this.state.secondaryButton = styles.secondaryButton || {};
    this.state.background = styles.background || {};
    this.state.overlay = styles.overlay || {};
    if (styles.syncButtonStyles === void 0) {
      this.state.secondaryButton = this.state.button;
      this.state.colors.secondaryButtonText = this.state.colors.buttonText;
      this.state.colors.secondaryButtonBackground = this.state.colors.buttonBackground;
      this.state.fonts.secondaryButton = this.state.fonts.button;
    }
    const bgColor = tinycolor(this.state.colors.background?.base ?? "#FFF");
    const cssVars = getCssVarNames(this.state, "--thn-tool");
    this.addCssVariables({
      ...cssVars,
      "--thn-tool-colors-icon-back": hexToRgba(styles.colors?.icon || "#000000", 0.15),
      "--thn-tool-colors-background": styles.colors?.background?.gradient ? `linear-gradient(to right bottom, ${styles.colors?.background?.base}, ${styles.colors?.background?.gradient})` : styles.colors?.background?.base,
      "--thn-tool-button-background-color": styles.button?.style === ButtonStyleKind.Solid ? styles.colors?.buttonBackground : "transparent",
      "--thn-tool-button-border": styles.button?.style === ButtonStyleKind.Outline ? `solid 2px ${styles.colors?.buttonBackground}` : "transparent",
      "--thn-tool-secondary-button-background-color": this.state.secondaryButton?.style === ButtonStyleKind.Solid ? this.state.colors?.secondaryButtonBackground : "transparent",
      "--thn-tool-secondary-button-border": this.state.secondaryButton?.style === ButtonStyleKind.Outline ? `solid 2px ${this.state.colors?.secondaryButtonBackground}` : "transparent",
      "--thn-tool-background-border": styles.background?.border?.color ? `solid ${cssVars["--thn-tool-background-border-thickness"]} ${styles.background.border.color}` : "none",
      "--thn-tool-focus-visible": bgColor.isLight() ? "#767676" : "#ADADAD"
    });
  }
  addCssVariables(cssVars) {
    Object.keys(cssVars).forEach((key) => {
      this.rootDom?.style.setProperty(key, cssVars[key] || "");
    });
  }
  getCssVariableValue(variableName) {
    return this.rootDom && getComputedStyle(this.rootDom).getPropertyValue(variableName);
  }
}

const __variableDynamicImportRuntimeHelper = (glob, path, segs) => {
  const v = glob[path];
  if (v) {
    return typeof v === "function" ? v() : Promise.resolve(v);
  }
  return new Promise((_, reject) => {
    (typeof queueMicrotask === "function" ? queueMicrotask : setTimeout)(
      reject.bind(
        null,
        new Error(
          "Unknown variable dynamic import: " + path + (path.split("/").length !== segs ? ". Note that variables only represent file names one level deep." : "")
        )
      )
    );
  });
};

class LiteralsStore {
  state = reactive({
    language: Locale.English,
    device: DeviceKind.Desktop,
    literals: {}
  });
  get device() {
    return this.state.device;
  }
  get language() {
    return this.state.language;
  }
  async initialize(language, device) {
    this.state.language = language ?? Locale.English;
    this.state.device = device ?? DeviceKind.Desktop;
    const literals = await __variableDynamicImportRuntimeHelper((/* #__PURE__ */ Object.assign({"../locales/ar.json": () => import('./ar.wwoAYPOB.js'),"../locales/bg.json": () => import('./bg.BDmVBxnv.js'),"../locales/ca.json": () => import('./ca.BWlHpXIv.js'),"../locales/cs.json": () => import('./cs.BdJqj03v.js'),"../locales/da.json": () => import('./da.B9UPpF3u.js'),"../locales/de.json": () => import('./de.CFGAjK2W.js'),"../locales/el.json": () => import('./el.CXUUDxl6.js'),"../locales/en.json": () => import('./en.CP13n5UB.js'),"../locales/es.json": () => import('./es.B0__L5ec.js'),"../locales/et.json": () => import('./et.Cf7eCnls.js'),"../locales/eu.json": () => import('./eu.C3_-U7dM.js'),"../locales/fi.json": () => import('./fi.NNdPsTG_.js'),"../locales/fr.json": () => import('./fr.DqGoljFE.js'),"../locales/he.json": () => import('./he.BPTaPrIW.js'),"../locales/hr.json": () => import('./hr.C2PAl4YX.js'),"../locales/hu.json": () => import('./hu.CMLGA4oL.js'),"../locales/id.json": () => import('./id.QIEE21mo.js'),"../locales/is.json": () => import('./is.DRxq706E.js'),"../locales/it.json": () => import('./it.JwyZcZxF.js'),"../locales/ja.json": () => import('./ja.C6uQ6CGi.js'),"../locales/ko.json": () => import('./ko.Cgdf2Vgf.js'),"../locales/lt.json": () => import('./lt.B9Bq0nUM.js'),"../locales/lv.json": () => import('./lv.CPcuyuXI.js'),"../locales/nl.json": () => import('./nl.xQZ2jJNQ.js'),"../locales/no.json": () => import('./no.BLLuXw5P.js'),"../locales/pl.json": () => import('./pl.Vh6qG0KK.js'),"../locales/pt.json": () => import('./pt.BG5gJ_Ky.js'),"../locales/ro.json": () => import('./ro.eJ3UbR9F.js'),"../locales/ru.json": () => import('./ru.3KKrbc04.js'),"../locales/sk.json": () => import('./sk.Drudljfj.js'),"../locales/sl.json": () => import('./sl.m0toqaOw.js'),"../locales/sr.json": () => import('./sr._62QDVnt.js'),"../locales/sv.json": () => import('./sv.CkhP_rJv.js'),"../locales/th.json": () => import('./th.BmgsFymL.js'),"../locales/tr.json": () => import('./tr.CdH4izH6.js'),"../locales/uk.json": () => import('./uk.BnseNhoM.js'),"../locales/vi.json": () => import('./vi.D0nAZFV6.js'),"../locales/zh-TW.json": () => import('./zh-TW.CY96HJ-h.js'),"../locales/zh.json": () => import('./zh.C8KS82ke.js')})), `../locales/${this.state.language}.json`, 3);
    this.state.literals = literals.default;
  }
  getLiteral(key, titleCase) {
    return titleCase ? toTitleCase(this.state.literals[key] || key) : this.state.literals[key] || key;
  }
  getLiteralAbb(key, titleCase) {
    const abbrKey = `${key}_abbr`;
    return this.getLiteral(abbrKey, titleCase);
  }
  getActivityLiteral(textKey, tokens = {}) {
    const interpolationValue = tokens[textKey];
    const baseLiteral = getPluralized(
      this.state.language,
      Number(interpolationValue),
      this.state.literals[activityKindLiteralMap[textKey]],
      this.state.literals,
      textKey
    );
    if (interpolationValue) {
      return baseLiteral?.replace("XXXXXX", interpolationValue);
    }
    return baseLiteral;
  }
  getPerkActiveLiteral(perkDescription, titleCase = false) {
    const activeText = perkDescription[this.state.language] ?? "";
    return titleCase ? toTitleCase(activeText) : activeText;
  }
  getPerkTooltipLiteral(textKey) {
    return this.state.literals[perkTooltipKindLiteralMap[textKey]];
  }
  getActiveModuleText(props, titleCase = false, tokens) {
    let activeText = props[this.device][this.language] ?? "";
    if (tokens) {
      const { candidates = [], replacement = {} } = tokens;
      candidates.forEach((token) => {
        const replacementValue = replacement[token];
        if (!replacementValue) {
          throw Error("The tool cannot be shown if any of the tokens do not have a correct replacement value");
        }
        activeText = activeText.replaceAll(`{${token}}`, replacementValue);
      });
    }
    return titleCase ? toTitleCase(activeText) : activeText;
  }
  getLocaleImageUrl(imageSettings) {
    switch (imageSettings.languageType) {
      case LocaleImageKind.Multi:
        return imageSettings.urls[this.language || Locale.English]?.edited;
      case LocaleImageKind.Single:
      default:
        return imageSettings.urls.edited;
    }
  }
  getLocaleImageAlt(imageSettings) {
    switch (imageSettings.languageType) {
      case LocaleImageKind.Multi:
        return imageSettings.alt[this.language || Locale.English];
      case LocaleImageKind.Single:
      default:
        return imageSettings.alt;
    }
  }
}

class ExtraDataStore {
  initTime = Date.now();
  state = reactive({
    builderMode: false,
    firstVisitDate: null,
    hotelTimezone: "Europe/Madrid",
    defaultAssets: void 0,
    countdownTimestamp: void 0,
    tokens: {},
    dynamicTags: []
  });
  get countdownInitTime() {
    return this.initTime;
  }
  get builderMode() {
    return this.state.builderMode;
  }
  get defaultAssets() {
    return this.state.defaultAssets;
  }
  get countdownTimestamp() {
    return this.state.countdownTimestamp;
  }
  get tokens() {
    return this.state.tokens;
  }
  get dynamicTags() {
    return this.state.dynamicTags;
  }
  initialize(data) {
    this.state.builderMode = !!data.builderMode;
    this.state.firstVisitDate = data.firstVisitDate;
    this.state.hotelTimezone = data.hotelTimezone;
    this.state.hotelTimezoneOffset = data.hotelTimezoneOffset;
    this.state.defaultAssets = data.defaultAssets;
    this.state.countdownTimestamp = data.countdownTimestamp;
    this.state.tokens = data.tokens;
    this.state.dynamicTags = data.dynamicTags;
  }
  getCountdownTimestamp(kind, details) {
    const {
      date = Date.now(),
      timezone,
      days = 0,
      hours = 0,
      minutes = 0
    } = details;
    this.initTime = Date.now();
    if (kind === CountdownKind.Time) {
      const daysMs = days * 24 * 60 * 60 * 1e3;
      const hoursMs = hours * 60 * 60 * 1e3;
      const minutesMs = minutes * 60 * 1e3;
      const discountMs = this.state.firstVisitDate ? this.initTime - this.state.firstVisitDate : 0;
      return this.initTime + daysMs + hoursMs + minutesMs - discountMs;
    }
    if (kind === CountdownKind.Date && date) {
      const dateEndDate = new Date(date);
      dateEndDate.setHours(hours);
      dateEndDate.setMinutes(minutes);
      if (timezone === CountdownTimezone.Hotel) {
        if (!this.state.hotelTimezoneOffset && this.state.hotelTimezone) {
          const timezoneDate = (/* @__PURE__ */ new Date()).toLocaleString("en-US", {
            timeZone: this.state.hotelTimezone
          });
          const timezoneMs = new Date(timezoneDate).getTime();
          const timezoneOffset = timezoneMs - (/* @__PURE__ */ new Date()).getTime();
          dateEndDate.setTime(dateEndDate.getTime() - timezoneOffset);
        } else if (this.state.hotelTimezoneOffset) {
          dateEndDate.setTime(dateEndDate.getTime() - this.state.hotelTimezoneOffset);
        }
      }
      return dateEndDate.getTime();
    }
    return null;
  }
}

const defaultLiteralsStore = new LiteralsStore();
const stores = {};
const injectionKeys = {};
function getRootUid() {
  const instance = getCurrentInstance();
  return instance?.uid || 0;
}
function getRootElement() {
  const instance = getCurrentInstance();
  return instance?.proxy?.$el;
}
function initializeStore(store) {
  const instance = getCurrentInstance();
  const hideFunction = instance?.exposed?.hide;
  store.inited = true;
  store.hideFunction = hideFunction || (() => {
  });
}
function updateStore(toolProps) {
  const storeUid = getRootUid();
  const rootElement = getRootElement();
  const store = stores[storeUid];
  if (store.inited) {
    store.language = toolProps.language;
    store.rtl = toolProps.language === Locale.Arabic || toolProps.language === Locale.Hebrew;
    store.device = toolProps.device;
    store.extraData.initialize(toolProps.extraData);
    store.handlers.initialize(store.hideFunction, toolProps.actions);
    store.literals.initialize(toolProps.language, toolProps.device);
    store.styles.initialize(toolProps.styles, rootElement);
  } else {
    throw new Error(`error updating store ${storeUid}: store not inited`);
  }
}
function createStore(toolProps) {
  const uid = getRootUid();
  const storeInjectionKey = Symbol("useStore injection key");
  const store = reactive({
    uid,
    inited: false,
    language: Locale.English,
    device: DeviceKind.Desktop,
    hideFunction: () => {
    },
    extraData: new ExtraDataStore(),
    handlers: new HandlersStore(),
    literals: defaultLiteralsStore,
    styles: new StylesStore()
  });
  stores[uid] = store;
  injectionKeys[uid] = storeInjectionKey;
  provide(storeInjectionKey, store);
  provide("rootUID", uid);
  onMounted(() => {
    initializeStore(store);
    updateStore(toolProps);
  });
  return store;
}
function useStore() {
  const uid = inject("rootUID");
  if (uid !== void 0) {
    return inject(injectionKeys[uid]);
  }
  throw new Error("No UID found to load the store");
}
function useLiterals() {
  return defaultLiteralsStore;
}

export { $e as $, ButtonState as B, __variableDynamicImportRuntimeHelper as _, toPascalCase as a, toKebabCase as b, camelToSnakeCase as c, getCornerRadius as d, appendPx as e, appendPercent as f, getPluralized as g, hexToRgba as h, appendEm as i, getPaddingLiteral as j, isObj as k, parseTimeLeft as l, toTitleCase as m, createStore as n, useStore as o, padNumber as p, useLiterals as q, sanitizeMessage as s, tinycolor as t, updateStore as u };
//# sourceMappingURL=useStore.B0k3KXLh.js.map
